import React, { useContext } from "react";
import { Badge, Button, Input, Spinner } from "reactstrap";
import Skeleton from "react-loading-skeleton";
import { format } from "date-fns";
import { ProductContext } from "../../store";
import { useLiveChatExecutive } from "./useLiveChatExecutive";
import { chatIdOf } from "../../services/liveChatApi";
import { parseAssistantMarkdown, ProductSlider } from "./conversationDisplay";

function sessionLabel(session) {
	if (!session) return "Visitor";
	return (
		session.customerName ||
		session.customer_name ||
		session.visitorId ||
		session.customerKey ||
		session.customer_key ||
		session.pageUrl ||
		session.page_url ||
		chatIdOf(session) ||
		"Visitor"
	);
}

function presenceLabel(session) {
	const st = String(session?.status || "").toLowerCase();
	if (st === "closed" || st === "ended") {
		return "Closed";
	}
	const stPresence = String(session?.presenceStatus || "").toLowerCase();
	if (stPresence === "idle") return "Idle";
	if (stPresence === "online") return "Online";
	if (stPresence === "left") return "Away";
	return "Unknown";
}

function presenceDotColor(session) {
	const st = String(session?.status || "").toLowerCase();
	if (st === "closed" || st === "ended") {
		return "#adb5bd";
	}
	const stPresence = String(session?.presenceStatus || "").toLowerCase();
	if (stPresence === "online") return "#28a745";
	if (stPresence === "idle") return "#fd7e14";
	if (stPresence === "left") return "#6c757d";
	return "#adb5bd";
}

function statusBadge(session) {
	const st = String(session?.status || "").toLowerCase();
	if (st === "closed" || st === "ended") {
		return <Badge color="secondary">Closed</Badge>;
	}
	if (st === "waiting" || st === "waiting_for_executive") {
		return <Badge color="warning">Waiting</Badge>;
	}
	if (st === "active") return <Badge color="success">Active</Badge>;
	return <Badge color="warning">Waiting</Badge>;
}

function sessionListKey(session) {
	if (!session) return null;
	return (
		chatIdOf(session) ||
		session.agentSessionId ||
		session.sessionId ||
		session.visitorId ||
		"session"
	);
}

const LiveChatTab = ({ websiteId, active, embedded = false }) => {
	const { userData, websiteData } = useContext(ProductContext);
	const userId = userData?.userId;
	const siteRow = websiteId ? websiteData?.[websiteId] ?? websiteData?.[Number(websiteId)] : null;
	const websiteBaseUrl = siteRow?.websiteURL != null ? String(siteRow.websiteURL).trim() : "";
	const {
		online,
		wsConnected,
		sessions,
		sessionsLoader,
		selectedSession,
		setSelectedSession,
		mergedThread,
		aiTurns,
		messagesLoader,
		draft,
		setDraft,
		sending,
		sendReply,
		handleClose,
		withdrawClose,
		closeConfirm,
		unread,
		isReadOnly,
	} = useLiveChatExecutive({ websiteId, userId, active });

	return (
		<div
			style={{
				display: "flex",
				height: embedded ? "100%" : "calc(100vh - 180px)",
				minHeight: embedded ? 0 : 400,
				border: embedded ? "none" : "1px solid #dee2e6",
				overflow: "hidden",
				backgroundColor: "#fff",
			}}
			className="chat-container"
		>
			<div
				style={{
					width: 320,
					minWidth: 320,
					borderRight: "1px solid #dee2e6",
					display: "flex",
					flexDirection: "column",
					backgroundColor: "#f8f9fa",
				}}
			>
				<div className="p-2 border-bottom bg-white d-flex align-items-center justify-content-between gap-2">
					<span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>Executive</span>
					<Badge color={online && wsConnected ? "success" : "secondary"} pill>
						{online && wsConnected ? "Online" : "Connecting…"}
					</Badge>
				</div>

				{sessionsLoader && sessions.length === 0 ? (
					<div style={{ padding: 12 }}>
						<Skeleton height={48} count={4} style={{ marginBottom: 8 }} />
					</div>
				) : sessions.length === 0 ? (
					<div className="p-4 text-muted text-center small">No live sessions</div>
				) : (
					<div style={{ overflowY: "auto", flex: 1 }}>
						{sessions.filter(Boolean).map((session) => {
							const id = sessionListKey(session);
							const chatId = chatIdOf(session);
							const isSelected = sessionListKey(selectedSession) === id;
							const hasUnread = chatId && unread[chatId] > 0;
							return (
								<div
									key={id}
									onClick={() => setSelectedSession(session)}
									style={{
										padding: "12px 14px",
										borderBottom: "1px solid #eee",
										cursor: "pointer",
										backgroundColor: isSelected ? "#e7f1ff" : "transparent",
									}}
								>
									<div className="d-flex align-items-start justify-content-between gap-2">
										<div style={{ minWidth: 0, flex: 1 }} className="d-flex align-items-start gap-2">
											<span
												title={presenceLabel(session)}
												style={{
													width: 8,
													height: 8,
													borderRadius: "50%",
													backgroundColor: presenceDotColor(session),
													flexShrink: 0,
													marginTop: 5,
												}}
											/>
											<div style={{ minWidth: 0, flex: 1 }}>
												<div style={{ fontSize: 13, fontWeight: 600, color: "#333" }} className="text-truncate">
													{sessionLabel(session)}
												</div>
												{(session.firstMessage || session.pageUrl || session.page_url) && (
													<div className="text-muted small text-truncate" style={{ fontSize: 11 }}>
														{session.firstMessage || session.pageUrl || session.page_url}
													</div>
												)}
												<div className="mt-1">{statusBadge(session)}</div>
											</div>
										</div>
										{hasUnread ? (
											<Badge color="danger" pill>
												{unread[chatId]}
											</Badge>
										) : null}
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>

			<div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
				{!selectedSession ? (
					<div className="d-flex align-items-center justify-content-center text-muted" style={{ flex: 1 }}>
						Select a live session
					</div>
				) : (
					<>
						<div
							className="d-flex align-items-center justify-content-between gap-2 flex-wrap"
							style={{
								padding: "12px 16px",
								borderBottom: "1px solid #dee2e6",
								backgroundColor: "#fff",
							}}
						>
							<div style={{ minWidth: 0 }} className="d-flex align-items-center gap-2">
								<span
									style={{
										width: 10,
										height: 10,
										borderRadius: "50%",
										backgroundColor: presenceDotColor(selectedSession),
										flexShrink: 0,
									}}
								/>
								<div style={{ minWidth: 0 }}>
									<h6 className="mb-0 text-truncate" style={{ fontSize: 14, fontWeight: 600 }}>
										{sessionLabel(selectedSession)}
									</h6>
									{selectedSession.customerEmail ? (
										<div className="text-muted small text-truncate">{selectedSession.customerEmail}</div>
									) : null}
									<div className="text-muted small">{statusBadge(selectedSession)}</div>
								</div>
							</div>
							<div className="d-flex gap-2 flex-shrink-0">
								{!isReadOnly ? (
									<Button size="sm" color="outline-danger" onClick={handleClose}>
										End chat
									</Button>
								) : null}
							</div>
						</div>

						{!isReadOnly && (closeConfirm.agentConfirmed || closeConfirm.userConfirmed) ? (
							<div className="px-3 py-2 border-bottom bg-light small">
								{closeConfirm.agentConfirmed && !closeConfirm.userConfirmed ? (
									<span>Waiting for customer to confirm closing this chat.</span>
								) : null}
								{closeConfirm.userConfirmed && !closeConfirm.agentConfirmed ? (
									<span className="me-2">Customer requested to end the chat.</span>
								) : null}
								{closeConfirm.userConfirmed && !closeConfirm.agentConfirmed ? (
									<Button size="sm" color="danger" className="me-2" onClick={handleClose}>
										Confirm close
									</Button>
								) : null}
								{closeConfirm.agentConfirmed && !closeConfirm.userConfirmed ? (
									<Button size="sm" color="link" className="p-0" onClick={withdrawClose}>
										Withdraw request
									</Button>
								) : null}
							</div>
						) : null}

						<div
							style={{
								flex: 1,
								overflowY: "auto",
								padding: 16,
								backgroundColor: "#f8f9fa",
							}}
						>
							{messagesLoader ? (
								<Skeleton height={40} count={4} style={{ marginBottom: 8 }} />
							) : mergedThread.length === 0 ? (
								<div className="text-muted text-center py-5">No messages yet</div>
							) : (
								mergedThread.map((item, idx) => {
									if (item.kind === "live_system") {
										return (
											<div key={`sys-${idx}`} className="text-center text-muted small my-3">
												{item.text}
											</div>
										);
									}
									if (item.kind === "live_message") {
										const isExec = item.senderType === "executive";
										const isCustomer = item.senderType === "customer";
										return (
											<div
												key={`live-${idx}`}
												className="d-flex mb-3"
												style={{ justifyContent: isExec ? "flex-end" : "flex-start" }}
											>
												<div
													style={{
														maxWidth: "85%",
														padding: "10px 14px",
														borderRadius: 12,
														backgroundColor: isExec ? "#d1e7dd" : isCustomer ? "#fff" : "#e9ecef",
														border: isCustomer ? "1px solid #dee2e6" : "none",
													}}
												>
													<div style={{ fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
														{item.text}
													</div>
													<div className="text-muted mt-1" style={{ fontSize: 11 }}>
														{isExec ? "You" : isCustomer ? "Customer" : item.senderType}
														{item.timestamp && ` · ${format(new Date(item.timestamp), "MM/dd/yyyy h:mm a")}`}
													</div>
												</div>
											</div>
										);
									}
									if (item.kind === "ai_turn_question") {
										return (
											<div key={`q-${idx}`} className="d-flex mb-3" style={{ justifyContent: "flex-start" }}>
												<div
													style={{
														maxWidth: "75%",
														padding: "10px 14px",
														borderRadius: 12,
														backgroundColor: "#fff",
														border: "1px solid #dee2e6",
													}}
												>
													<div style={{ fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
														{item.text}
													</div>
													<div className="text-muted mt-1" style={{ fontSize: 11 }}>
														Customer
														{item.timestamp && ` · ${format(new Date(item.timestamp), "MM/dd/yyyy h:mm a")}`}
													</div>
												</div>
											</div>
										);
									}
									const turn = aiTurns[item.turnIdx];
									return (
										<div key={`a-${idx}`} className="d-flex mb-3" style={{ justifyContent: "flex-end" }}>
											<div
												style={{
													maxWidth: "85%",
													padding: "10px 14px",
													borderRadius: 12,
													backgroundColor: "#e9ecef",
												}}
											>
												<div
													className="cb-sa-conv-msg-text"
													style={{ fontSize: 14, wordBreak: "break-word" }}
													dangerouslySetInnerHTML={{ __html: parseAssistantMarkdown(item.text || "") }}
												/>
												{turn ? <ProductSlider products={turn.products} websiteBaseUrl={websiteBaseUrl} /> : null}
												<div className="text-muted mt-1" style={{ fontSize: 11 }}>
													AI Assistant
													{item.timestamp && ` · ${format(new Date(item.timestamp), "MM/dd/yyyy h:mm a")}`}
												</div>
											</div>
										</div>
									);
								})
							)}
						</div>

						{!isReadOnly ? (
							<div
								className="d-flex gap-2 align-items-end border-top bg-white p-3"
								style={{ borderColor: "#dee2e6" }}
							>
								<Input
									type="textarea"
									rows={2}
									value={draft}
									onChange={(e) => setDraft(e.target.value)}
									placeholder="Type a reply…"
									onKeyDown={(e) => {
										if (e.key === "Enter" && !e.shiftKey) {
											e.preventDefault();
											sendReply();
										}
									}}
									disabled={sending}
								/>
								<Button color="primary" onClick={sendReply} disabled={sending || !draft.trim()}>
									{sending ? <Spinner size="sm" /> : "Send"}
								</Button>
							</div>
						) : (
							<div
								className="border-top bg-white px-3 py-2 text-muted small"
								style={{ borderColor: "#dee2e6" }}
							>
								This conversation is closed (read-only).
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default LiveChatTab;
